import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { Button, Card, Input, Empty, Spinner } from "@/components/ui/Primitives";
import { api, formatErr } from "@/lib/api";
import { Plus, Pencil, Trash2, X, Truck } from "lucide-react";

const empty = { nome: "", cpf: "", telefone: "", placas: [], observacoes: "", ativo: true };

// Normaliza placa: uppercase, remove tudo que não for A-Z/0-9
const normPlaca = (p) => (p || "").toUpperCase().replace(/[^A-Z0-9]/g, "");

export default function Motoristas() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [placaInput, setPlacaInput] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("");

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/motoristas"); setItems(data); }
    catch (e) { setErr(formatErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setPlacaInput(""); setOpen(true); setErr(""); };
  const openEdit = (m) => {
    setEditing(m);
    setForm({
      nome: m.nome || "",
      cpf: m.cpf || "",
      telefone: m.telefone || "",
      placas: [...(m.placas || [])],
      observacoes: m.observacoes || "",
      ativo: m.ativo !== false,
    });
    setPlacaInput("");
    setOpen(true); setErr("");
  };

  const addPlaca = () => {
    const p = normPlaca(placaInput);
    if (!p) return;
    if (form.placas.includes(p)) { setErr(`Placa ${p} já adicionada.`); return; }
    setForm({ ...form, placas: [...form.placas, p] });
    setPlacaInput(""); setErr("");
  };
  const removePlaca = (p) => setForm({ ...form, placas: form.placas.filter((x) => x !== p) });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nome.trim()) { setErr("Informe o nome do motorista."); return; }
    setBusy(true); setErr("");
    try {
      const payload = { ...form, placas: form.placas.map(normPlaca).filter(Boolean) };
      if (editing) await api.put(`/motoristas/${editing.id}`, payload);
      else await api.post("/motoristas", payload);
      setOpen(false); await load();
    } catch (e2) { setErr(formatErr(e2)); }
    finally { setBusy(false); }
  };

  const onDelete = async (id, nome) => {
    if (!window.confirm(`Excluir motorista "${nome}"?`)) return;
    try { await api.delete(`/motoristas/${id}`); await load(); }
    catch (e) { alert(formatErr(e)); }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const filtered = items.filter((m) => {
    if (!filter.trim()) return true;
    const q = filter.trim().toLowerCase();
    return (m.nome || "").toLowerCase().includes(q)
      || (m.cpf || "").toLowerCase().includes(q)
      || (m.placas || []).some((p) => p.toLowerCase().includes(q));
  });

  return (
    <Layout
      title="Motoristas"
      subtitle="Cadastro de motoristas e placas usados nas NFs de entrada"
      actions={<Button onClick={openNew} data-testid="new-motorista-button"><Plus size={16} /> Novo Motorista</Button>}
    >
      <div className="mb-4 max-w-md">
        <Input
          placeholder="Buscar por nome, CPF ou placa..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          data-testid="motoristas-search"
        />
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Spinner size={28} /></div>
      ) : filtered.length === 0 ? (
        <Card><Empty title="Nenhum motorista cadastrado" hint="Adicione o primeiro para começar a usar no cadastro de NFs." /></Card>
      ) : (
        <Card className="overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-stone-50 border-b border-ink-200">
              <tr className="text-left text-xs font-bold uppercase tracking-wider text-ink-500">
                <th className="px-4 py-3">Nome</th>
                <th className="px-4 py-3">CPF</th>
                <th className="px-4 py-3">Telefone</th>
                <th className="px-4 py-3">Placas</th>
                <th className="px-4 py-3 w-1">Ações</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((m) => (
                <tr key={m.id} className="border-b border-ink-200 last:border-0 hover:bg-stone-50 transition-colors" data-testid={`motorista-row-${m.id}`}>
                  <td className="px-4 py-3 font-medium text-ink-900 flex items-center gap-2">
                    <Truck size={14} className="text-ink-400" />
                    {m.nome}
                    {m.ativo === false && <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-stone-200 text-stone-600">inativo</span>}
                  </td>
                  <td className="px-4 py-3 font-mono text-ink-700">{m.cpf || "—"}</td>
                  <td className="px-4 py-3 text-ink-700">{m.telefone || "—"}</td>
                  <td className="px-4 py-3">
                    {(m.placas || []).length === 0 ? (
                      <span className="text-ink-400 text-xs italic">nenhuma</span>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {(m.placas || []).map((p) => (
                          <span key={p} className="font-mono text-xs px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 border border-emerald-300">{p}</span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <button onClick={() => openEdit(m)} className="p-1.5 rounded-md hover:bg-stone-100 text-ink-700" data-testid={`motorista-edit-${m.id}`}><Pencil size={16} /></button>
                    <button onClick={() => onDelete(m.id, m.nome)} className="p-1.5 rounded-md hover:bg-red-100 text-danger" data-testid={`motorista-delete-${m.id}`}><Trash2 size={16} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {open && (
        <div className="fixed inset-0 z-50 bg-stone-900/50 backdrop-blur-sm flex items-center justify-center p-4 print:hidden">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg animate-fade-in">
            <div className="flex items-center justify-between px-6 py-4 border-b border-ink-200">
              <h3 className="font-heading font-bold text-ink-900">{editing ? "Editar Motorista" : "Novo Motorista"}</h3>
              <button onClick={() => setOpen(false)} className="text-ink-500 hover:text-ink-900"><X size={18} /></button>
            </div>
            <form onSubmit={submit} className="p-6 space-y-4">
              <Input label="Nome" required value={form.nome} onChange={set("nome")} data-testid="motorista-nome" />
              <div className="grid grid-cols-2 gap-3">
                <Input label="CPF" value={form.cpf} onChange={set("cpf")} data-testid="motorista-cpf" />
                <Input label="Telefone" value={form.telefone} onChange={set("telefone")} data-testid="motorista-telefone" />
              </div>

              {/* Placas */}
              <div>
                <div className="block text-xs font-bold uppercase tracking-[0.1em] text-ink-500 mb-1.5">
                  Placas do motorista <span className="text-ink-400 font-normal normal-case">(pode adicionar várias)</span>
                </div>
                <div className="flex gap-2">
                  <input
                    value={placaInput}
                    onChange={(e) => setPlacaInput(e.target.value.toUpperCase())}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addPlaca(); } }}
                    placeholder="ABC1D23"
                    maxLength={8}
                    className="flex-1 rounded-md bg-stone-50 border border-stone-300 px-3 py-2 font-mono uppercase focus:outline-none focus:ring-2 focus:ring-brand-500 text-sm"
                    data-testid="motorista-placa-input"
                  />
                  <Button type="button" variant="secondary" onClick={addPlaca} data-testid="motorista-placa-add"><Plus size={14} /> Adicionar</Button>
                </div>
                {form.placas.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5" data-testid="motorista-placas-list">
                    {form.placas.map((p) => (
                      <span key={p} className="inline-flex items-center gap-1 font-mono text-sm px-2 py-1 rounded bg-emerald-100 text-emerald-800 border border-emerald-300">
                        {p}
                        <button type="button" onClick={() => removePlaca(p)} className="text-emerald-700 hover:text-red-700" title="Remover placa"><X size={12} /></button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <Input label="Observações" value={form.observacoes} onChange={set("observacoes")} data-testid="motorista-observacoes" />

              <label className="flex items-center gap-2 text-sm text-ink-700 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={form.ativo}
                  onChange={(e) => setForm({ ...form, ativo: e.target.checked })}
                  className="w-4 h-4 accent-emerald-600"
                  data-testid="motorista-ativo"
                />
                Motorista ativo (aparece no autocomplete das NFs)
              </label>

              {err && <div className="text-sm text-danger bg-red-50 border border-red-200 px-3 py-2 rounded-md">{err}</div>}
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="secondary" onClick={() => setOpen(false)}>Cancelar</Button>
                <Button type="submit" disabled={busy} data-testid="motorista-submit">{busy ? <Spinner size={16} /> : "Salvar"}</Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}
